import { useEffect, useState } from 'react'

import { Loader } from '@/components/ui'
import { useAutoScroll } from '@/hooks'
import { forgetDraftIfUnchanged } from '../composerDrafts'
import { useLinkPreviewCutoff } from '../hooks/useLinkPreviewCutoff'
import { useNewReplyBelow } from '../hooks/useNewReplyBelow'
import { usePendingAttachments } from '../hooks/usePendingAttachments'
import { useReportTicketRead } from '../hooks/useReportTicketRead'
import { useThreadAnnouncement } from '../hooks/useThreadAnnouncement'
import { useUnreadEntryWindow } from '../hooks/useUnreadEntryWindow'
import {
  type PendingMessage,
  pendingMessagesForTicket,
} from '../optimisticMessages'
import {
  useSupportActions,
  useSupportActiveMessages,
  useSupportActiveTicket,
  useSupportLoadingTicket,
  useSupportMutations,
  useSupportPendingMessages,
} from '../store'
import { buildThreadRows } from '../threadTimeline'
import { useStaffTyping } from '../typingStore'
import { AttachmentDropZone } from './AttachmentDropZone'
import { NewReplyPill } from './NewReplyPill'
import { StaffTypingIndicator, staffTypingLabel } from './StaffTypingIndicator'
import { ThreadTranscript } from './ThreadTranscript'
import { type RestoredReply, TicketComposer } from './TicketComposer'
import { TicketThreadHeader } from './TicketThreadHeader'

export const TicketThread = ({ onBack }: { onBack: () => void }) => {
  const ticket = useSupportActiveTicket()
  const messages = useSupportActiveMessages()
  const loading = useSupportLoadingTicket()
  const { markTicketSeen } = useSupportActions()
  const { sendMessage, retryPendingMessage, discardPendingMessage } =
    useSupportMutations()
  const staffTyping = useStaffTyping()
  const ticketPublicId = ticket?.publicId ?? ''

  // Captured before the effect below stamps the watermark forward, so the entry
  // marker still knows where the unread replies start.
  const unreadWindow = useUnreadEntryWindow(ticket, messages)
  const linkPreviewSince = useLinkPreviewCutoff(
    ticketPublicId,
    messages,
    loading,
  )
  const allPendingMessages = useSupportPendingMessages()
  const pendingMessages = pendingMessagesForTicket(
    allPendingMessages,
    ticketPublicId,
  )
  const attachments = usePendingAttachments(ticketPublicId)
  const [restoredReply, setRestoredReply] = useState<RestoredReply | null>(null)

  const {
    containerRef,
    handleScroll,
    scrollToBottom,
    stickToBottom,
    isAwayFromBottom,
  } = useAutoScroll(`${messages.length}:${pendingMessages.length}`)
  const hasNewReplyBelow = useNewReplyBelow(
    isAwayFromBottom,
    messages.length + pendingMessages.length,
  )

  const typingHere = staffTyping?.ticketPublicId === ticketPublicId
  const announcement = useThreadAnnouncement(
    ticketPublicId,
    messages,
    typingHere ? staffTypingLabel(staffTyping?.staffName ?? null) : null,
  )

  useReportTicketRead(ticket, messages)

  useEffect(() => {
    // Only stamp the watermark once the thread actually loaded; a failed
    // fetch must not clear the unread dot.
    if (!ticket || loading || messages.length === 0) return
    markTicketSeen(ticket.publicId, ticket.lastMessageAt)
  }, [ticket, loading, messages.length, markTicketSeen])

  if (!ticket) return null

  const rows = buildThreadRows({
    messages,
    pendingMessages,
    unreadWindow,
    linkPreviewSince,
    now: new Date(),
  })

  const forgetDraftOnDelivery = async (
    body: string,
    delivery: Promise<boolean>,
  ) => {
    if (await delivery) forgetDraftIfUnchanged(ticketPublicId, body)
  }

  const submitReply = (body: string) => {
    void forgetDraftOnDelivery(
      body,
      sendMessage(ticket.publicId, body, attachments.files),
    )
    attachments.clearFiles()
    // Sending always follows the conversation down, so the reply the user just
    // wrote is never hidden below the fold.
    stickToBottom()
  }

  const retryPending = (pending: PendingMessage) => {
    void forgetDraftOnDelivery(pending.body, retryPendingMessage(pending.id))
  }

  const discardPending = (pending: PendingMessage) => {
    discardPendingMessage(pending.id)
    setRestoredReply({ body: pending.body })
    attachments.addFiles(pending.files)
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <TicketThreadHeader ticket={ticket} onBack={onBack} />

      <AttachmentDropZone
        onFilesDropped={attachments.addFiles}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="relative flex min-h-0 flex-1 flex-col">
          <div
            ref={containerRef}
            onScroll={handleScroll}
            className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-blue-1/50 px-5 py-4"
          >
            {loading && messages.length === 0 && (
              <div className="flex justify-center py-8">
                <Loader />
              </div>
            )}

            {/* mt-auto so a short conversation hugs the composer instead of
                floating at the top of an empty pane. */}
            <div className="mt-auto space-y-5">
              <ThreadTranscript
                rows={rows}
                onRetryPending={retryPending}
                onDiscardPending={discardPending}
              />
            </div>
          </div>

          {hasNewReplyBelow && <NewReplyPill onClick={scrollToBottom} />}
        </div>

        {/* Always on screen, empty when nobody is typing. A row that came and
            went would resize the message list and slide the newest reply out of
            view with no scroll to bring it back. */}
        <div className="flex h-8 shrink-0 items-center bg-blue-1/50 px-5">
          {typingHere && (
            <StaffTypingIndicator staffName={staffTyping?.staffName ?? null} />
          )}
        </div>

        <TicketComposer
          ticket={ticket}
          attachments={attachments}
          pendingBodies={pendingMessages.map((pending) => pending.body)}
          restoredReply={restoredReply}
          onSubmit={submitReply}
        />
      </AttachmentDropZone>

      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </div>
  )
}
